import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Box, Button, FormControl, FormLabel, Stack, TextField } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { updateCompanyInfo, updateCompanyInfoReset } from "../../state/companyList/companyListSlice";
import { selectCompanyListState } from "../../state/companyList/companyListSelector";

const slugify = (str) => {
  return String(str || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9 -]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
};

const EditCompanyForm = ({ onClose, companyInfo }) => {
  const dispatch = useDispatch();
  const { isUpdating, isUpdateSuccess } = useSelector(selectCompanyListState);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: companyInfo?.name || "",
      slug: companyInfo?.slug || "",
    },
  });

  useEffect(() => {
    if (isUpdateSuccess) {
      onClose();
      dispatch(updateCompanyInfoReset());
    }
  }, [isUpdateSuccess, onClose, dispatch]);

  const handleNameChange = (e) => {
    const newName = e.target.value;
    // Auto populate slug if current slug was empty or matches slugified old name
    const currentSlug = companyInfo?.slug || "";
    const oldSlugified = slugify(companyInfo?.name || "");
    if (!currentSlug || currentSlug === oldSlugified) {
      setValue("slug", slugify(newName), { shouldValidate: true });
    }
  };

  const onSubmit = (data) => {
    dispatch(
      updateCompanyInfo({
        companyId: companyInfo.id,
        name: data.name,
        slug: data.slug,
      })
    );
  };

  return (
    <Box component="form" noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)} sx={{ pt: 1 }}>
      <Stack spacing={3}>
        <FormControl fullWidth required error={!!errors.name}>
          <FormLabel sx={{ mb: 1, fontWeight: 500, color: "text.primary" }}>Company Name</FormLabel>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter company name"
            {...register("name", {
              required: "Company name is required",
              minLength: { value: 2, message: "Must be at least 2 characters" },
              maxLength: { value: 100, message: "Cannot exceed 100 characters" },
              onChange: handleNameChange,
            })}
            error={!!errors.name}
            helperText={errors.name?.message}
          />
        </FormControl>

        <FormControl fullWidth required error={!!errors.slug}>
          <FormLabel sx={{ mb: 1, fontWeight: 500, color: "text.primary" }}>Company Slug</FormLabel>
          <TextField
            fullWidth
            size="small"
            placeholder="enter-company-slug"
            {...register("slug", {
              required: "Company slug is required",
              minLength: { value: 2, message: "Must be at least 2 characters" },
              maxLength: { value: 100, message: "Cannot exceed 100 characters" },
              pattern: {
                value: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
                message: "Must contain only lowercase letters, numbers, and hyphens (e.g., company-slug)",
              },
            })}
            error={!!errors.slug}
            helperText={errors.slug?.message}
          />
        </FormControl>

        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
          <Button variant="outlined" color="inherit" onClick={onClose} disabled={isUpdating}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="primary" disabled={isUpdating}>
            {isUpdating ? "Saving..." : "Save Changes"}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default EditCompanyForm;
